import { useEffect, useId, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogField, DialogFooter, dialogInputStyle } from "@/components/ui/Dialog";
import { api, ApiError } from "@/lib/api";
import { t } from "@/lib/strings";
import type {
  MondayBoardPlan,
  MondayBoardSummary,
  MondayColumnOverride,
  MondayImportReport,
  MondayPlanColumn,
} from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

type Step = "token" | "boards" | "running" | "report";

interface Options {
  subitems: boolean;
  comments: boolean;
  files: boolean;
}

// One board's slot in a multi-board batch run.
interface BatchItem {
  id: string;
  name: string;
  status: "pending" | "running" | "done" | "error";
  report?: MondayImportReport;
  error?: string;
}

// Pause between boards in a batch so Monday's per-minute complexity budget
// refills (the importer also backs off per-request; this just spaces the runs).
const PACE_MS = 2000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Per-column edit state, seeded from the server plan and sent back as overrides.
interface ColEdit {
  include: boolean;
  name: string;
  flow_type: string;
  merge_into: string | null; // null = own column
}

type Edits = Record<string, ColEdit>;

const TYPE_LABELS: Record<string, string> = {
  text: "Text (kurz)",
  long_text: "Text (lang)",
  status: "Status",
  person: "Person",
  date: "Datum",
  timeline: "Zeitraum",
  number: "Zahl",
  checkbox: "Checkbox",
  tags: "Schlagwörter",
  link: "Link",
  file: "Datei",
  lead_time: "Vorlaufzeit",
};

function typeLabel(value: string): string {
  return TYPE_LABELS[value] ?? value;
}

function compatible(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  return (a === "date" && b === "timeline") || (a === "timeline" && b === "date");
}

function seedEdits(plan: MondayBoardPlan): Edits {
  const out: Edits = {};
  for (const c of plan.columns) {
    out[c.key] = {
      include: c.include,
      name: c.is_primary ? c.flow_name : c.own_name,
      flow_type: c.flow_type ?? "text",
      merge_into: c.merge_into,
    };
  }
  return out;
}

export function ImportMondayDialog({ open, onOpenChange, workspaceId }: Props) {
  const tokenId = useId();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("token");
  const [token, setToken] = useState("");
  const [boards, setBoards] = useState<MondayBoardSummary[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [plan, setPlan] = useState<MondayBoardPlan | null>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [planBusy, setPlanBusy] = useState(false);
  const [options, setOptions] = useState<Options>({ subitems: true, comments: true, files: true });
  const [report, setReport] = useState<MondayImportReport | null>(null);
  const [batch, setBatch] = useState<BatchItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-board mapping review only when exactly one board is selected; 2+ boards
  // import with the smart defaults (reviewing 21 mappings defeats the purpose).
  const singleId = checked.size === 1 ? ([...checked][0] ?? null) : null;

  useEffect(() => {
    if (!open) {
      setStep("token");
      setToken("");
      setBoards([]);
      setChecked(new Set());
      setPlan(null);
      setEdits({});
      setPlanBusy(false);
      setOptions({ subitems: true, comments: true, files: true });
      setReport(null);
      setBatch([]);
      setBusy(false);
      setError(null);
    }
  }, [open]);

  // Load (or clear) the column plan for the single-selected board.
  useEffect(() => {
    if (!singleId) {
      setPlan(null);
      setEdits({});
      return;
    }
    let cancelled = false;
    setPlan(null);
    setEdits({});
    setPlanBusy(true);
    api
      .mondayBoardPlan(token.trim(), singleId)
      .then((result) => {
        if (cancelled) return;
        setPlan(result);
        setEdits(seedEdits(result));
      })
      .catch(() => {
        // The plan is a nice-to-have; the backend falls back to smart defaults
        // when no overrides arrive, so a failure here doesn't block the import.
        if (!cancelled) setPlan(null);
      })
      .finally(() => {
        if (!cancelled) setPlanBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [singleId, token]);

  function describe(err: unknown): string {
    if (err instanceof ApiError) return err.message;
    if (err instanceof Error) return err.message;
    return t.unknownError;
  }

  async function connect() {
    if (token.trim().length < 10 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.mondayPreviewBoards(token.trim());
      setBoards(result);
      setStep("boards");
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  function toggleBoard(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setAll(all: boolean) {
    setChecked(all ? new Set(boards.map((b) => b.id)) : new Set());
  }

  async function runImport() {
    if (!singleId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const column_overrides: MondayColumnOverride[] = plan
        ? plan.columns
            .map((c): MondayColumnOverride | null => {
              const e = edits[c.key];
              if (!e) return null;
              return {
                key: c.key,
                include: e.include,
                name: e.name,
                flow_type: e.flow_type,
                merge_into: e.merge_into ?? "",
              };
            })
            .filter((x): x is MondayColumnOverride => x !== null)
        : [];
      const result = await api.mondayImportBoard({
        token: token.trim(),
        monday_board_id: singleId,
        workspace_id: workspaceId,
        import_subitems: options.subitems,
        import_comments: options.comments,
        import_files: options.files,
        column_overrides,
      });
      setReport(result);
      setStep("report");
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  // Multi-board: import each selected board sequentially with the smart defaults,
  // pacing between boards, accumulating per-board outcomes into the batch list.
  async function runBatch() {
    if (checked.size < 2 || busy) return;
    const items: BatchItem[] = boards
      .filter((b) => checked.has(b.id))
      .map((b) => ({ id: b.id, name: b.name, status: "pending" }));
    setBatch(items);
    setError(null);
    setStep("running");
    setBusy(true);
    for (const [i, item] of items.entries()) {
      setBatch((prev) => prev.map((x, idx) => (idx === i ? { ...x, status: "running" } : x)));
      try {
        const result = await api.mondayImportBoard({
          token: token.trim(),
          monday_board_id: item.id,
          workspace_id: workspaceId,
          import_subitems: options.subitems,
          import_comments: options.comments,
          import_files: options.files,
          column_overrides: [],
        });
        setBatch((prev) =>
          prev.map((x, idx) => (idx === i ? { ...x, status: "done", report: result } : x)),
        );
      } catch (err) {
        setBatch((prev) =>
          prev.map((x, idx) => (idx === i ? { ...x, status: "error", error: describe(err) } : x)),
        );
      }
      if (i < items.length - 1) await sleep(PACE_MS);
    }
    setBusy(false);
  }

  function startImport() {
    if (checked.size >= 2) void runBatch();
    else void runImport();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      title={t.mondayImportTitle}
      description={t.mondayImportDescription}
      width={680}
    >
      {step === "token" ? (
        <TokenStep
          tokenId={tokenId}
          token={token}
          onToken={setToken}
          onConnect={connect}
          onCancel={() => onOpenChange(false)}
          busy={busy}
          error={error}
        />
      ) : null}

      {step === "boards" ? (
        <BoardsStep
          boards={boards}
          checked={checked}
          singleId={singleId}
          onToggle={toggleBoard}
          onSetAll={setAll}
          plan={plan}
          planBusy={planBusy}
          edits={edits}
          onEdit={(key, patch) =>
            setEdits((prev) => {
              const base = prev[key];
              if (!base) return prev;
              return { ...prev, [key]: { ...base, ...patch } };
            })
          }
          onReset={() => plan && setEdits(seedEdits(plan))}
          options={options}
          onOptions={setOptions}
          onBack={() => setStep("token")}
          onImport={startImport}
          busy={busy}
          error={error}
        />
      ) : null}

      {step === "running" ? (
        <RunningStep
          batch={batch}
          busy={busy}
          onClose={() => onOpenChange(false)}
          onMore={() => {
            setBatch([]);
            setChecked(new Set());
            setStep("boards");
          }}
        />
      ) : null}

      {step === "report" && report ? (
        <ReportStep
          report={report}
          onOpenBoard={() => {
            if (report.flow_board_id) {
              onOpenChange(false);
              void navigate({ to: "/b/$boardId", params: { boardId: report.flow_board_id } });
            }
          }}
          onAnother={() => {
            setReport(null);
            setChecked(new Set());
            setPlan(null);
            setEdits({});
            setStep("boards");
          }}
          onClose={() => onOpenChange(false)}
        />
      ) : null}
    </Dialog>
  );
}

function TokenStep(props: {
  tokenId: string;
  token: string;
  onToken: (v: string) => void;
  onConnect: () => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <div>
      <DialogField
        label={t.mondayTokenLabel}
        htmlFor={props.tokenId}
        hint={t.mondayTokenHint}
        error={props.error ?? undefined}
      >
        <input
          id={props.tokenId}
          type="password"
          value={props.token}
          onChange={(e) => props.onToken(e.target.value)}
          placeholder={t.mondayTokenPlaceholder}
          autoComplete="off"
          name="monday-token"
          style={dialogInputStyle}
          onKeyDown={(e) => {
            if (e.key === "Enter") props.onConnect();
          }}
        />
      </DialogField>
      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={props.onCancel}
          disabled={props.busy}
        >
          {t.cancel}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={props.onConnect}
          disabled={props.token.trim().length < 10 || props.busy}
        >
          {props.busy ? t.mondayConnecting : t.mondayConnect}
        </Button>
      </DialogFooter>
    </div>
  );
}

function BoardsStep(props: {
  boards: MondayBoardSummary[];
  checked: Set<string>;
  singleId: string | null;
  onToggle: (id: string) => void;
  onSetAll: (all: boolean) => void;
  plan: MondayBoardPlan | null;
  planBusy: boolean;
  edits: Edits;
  onEdit: (key: string, patch: Partial<ColEdit>) => void;
  onReset: () => void;
  options: Options;
  onOptions: (o: Options) => void;
  onBack: () => void;
  onImport: () => void;
  busy: boolean;
  error: string | null;
}) {
  const count = props.checked.size;
  const allSelected = props.boards.length > 0 && count === props.boards.length;
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--ink-2)" }}>
          {t.mondaySelectBoards}
          {count > 0 ? (
            <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>
              {" · "}
              {t.mondaySelectedCount.replace("{count}", String(count))}
            </span>
          ) : null}
        </div>
        {props.boards.length > 0 ? (
          <button type="button" onClick={() => props.onSetAll(!allSelected)} style={resetBtnStyle}>
            {allSelected ? t.mondayDeselectAll : t.mondaySelectAll}
          </button>
        ) : null}
      </div>
      <div style={listStyle}>
        {props.boards.map((b) => {
          const on = props.checked.has(b.id);
          return (
            <label
              key={b.id}
              style={{
                ...boardRowStyle,
                cursor: "pointer",
                background: on ? "var(--surface-soft)" : "transparent",
                borderColor: on ? "var(--color-brand-blue)" : "var(--hairline)",
              }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => props.onToggle(b.id)}
                style={{ flexShrink: 0 }}
              />
              <span
                style={{
                  fontWeight: 500,
                  color: "var(--ink-1)",
                  flex: "1 1 auto",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {b.name}
              </span>
              {typeof b.items_count === "number" ? (
                <span style={{ fontSize: 11.5, color: "var(--ink-4)", flexShrink: 0 }}>
                  {t.mondayBoardItems.replace("{count}", String(b.items_count))}
                </span>
              ) : null}
            </label>
          );
        })}
        {props.boards.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "var(--ink-4)", padding: "8px 2px" }}>—</div>
        ) : null}
      </div>

      {count >= 2 ? (
        <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 10, lineHeight: 1.4 }}>
          {t.mondayBatchDefaultsNote}
        </div>
      ) : null}

      {props.singleId && props.planBusy ? (
        <div style={{ fontSize: 12.5, color: "var(--ink-4)", marginTop: 12 }}>
          {t.mondayConnecting}
        </div>
      ) : null}
      {props.singleId && props.plan ? (
        <MappingEditor
          plan={props.plan}
          edits={props.edits}
          onEdit={props.onEdit}
          onReset={props.onReset}
        />
      ) : null}

      <div style={{ fontSize: 12, fontWeight: 500, color: "var(--ink-2)", margin: "14px 0 6px" }}>
        {t.mondayOptionsTitle}
      </div>
      <OptionRow
        label={t.mondayOptionSubitems}
        checked={props.options.subitems}
        onChange={(v) => props.onOptions({ ...props.options, subitems: v })}
      />
      <OptionRow
        label={t.mondayOptionComments}
        checked={props.options.comments}
        onChange={(v) => props.onOptions({ ...props.options, comments: v })}
      />
      <OptionRow
        label={t.mondayOptionFiles}
        checked={props.options.files}
        onChange={(v) => props.onOptions({ ...props.options, files: v })}
      />

      {props.busy ? (
        <div style={{ fontSize: 12.5, color: "var(--color-brand-blue)", marginTop: 12 }}>
          {t.mondayImporting}
        </div>
      ) : null}
      {props.error ? (
        <div style={{ fontSize: 12, color: "var(--danger)", marginTop: 10 }}>{props.error}</div>
      ) : null}

      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={props.onBack}
          disabled={props.busy}
        >
          {t.back}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={props.onImport}
          disabled={count === 0 || props.busy}
        >
          {count >= 2
            ? t.mondayImportSelected.replace("{count}", String(count))
            : t.mondayStartImport}
        </Button>
      </DialogFooter>
    </div>
  );
}

function statusColor(s: BatchItem["status"]): string {
  if (s === "done") return "#1F7A4D";
  if (s === "error") return "var(--danger)";
  if (s === "running") return "var(--color-brand-blue)";
  return "var(--ink-4)";
}

function statusGlyph(s: BatchItem["status"]): string {
  if (s === "done") return "✓";
  if (s === "error") return "✗";
  if (s === "running") return "…";
  return "•";
}

function statusLabel(b: BatchItem): string {
  if (b.status === "done" && b.report) {
    return `${b.report.rows} ${t.mondayReportRows} · ${b.report.columns_created} ${t.mondayReportColumns}`;
  }
  if (b.status === "error") return b.error ?? t.mondayBatchStatusError;
  if (b.status === "running") return t.mondayBatchStatusRunning;
  return t.mondayBatchStatusPending;
}

function RunningStep(props: {
  batch: BatchItem[];
  busy: boolean;
  onClose: () => void;
  onMore: () => void;
}) {
  const total = props.batch.length;
  const done = props.batch.filter((b) => b.status === "done").length;
  const failed = props.batch.filter((b) => b.status === "error").length;
  const finished = done + failed;
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)", marginBottom: 4 }}>
        {props.busy
          ? t.mondayBatchRunning
              .replace("{done}", String(finished))
              .replace("{total}", String(total))
          : t.mondayImportDone}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginBottom: 10, lineHeight: 1.4 }}>
        {props.busy
          ? t.mondayBatchPacingHint
          : t.mondayBatchDone.replace("{ok}", String(done)).replace("{failed}", String(failed))}
      </div>
      <div style={{ ...listStyle, maxHeight: 320, gap: 4 }}>
        {props.batch.map((b) => (
          <div key={b.id} style={{ ...boardRowStyle, cursor: "default" }}>
            <span
              style={{
                flexShrink: 0,
                width: 16,
                textAlign: "center",
                fontWeight: 700,
                color: statusColor(b.status),
              }}
            >
              {statusGlyph(b.status)}
            </span>
            <span
              style={{
                fontWeight: 500,
                color: "var(--ink-1)",
                flex: "1 1 auto",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {b.name}
            </span>
            <span
              style={{
                fontSize: 11.5,
                color: b.status === "error" ? "var(--danger)" : "var(--ink-4)",
                flexShrink: 0,
                maxWidth: 220,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={statusLabel(b)}
            >
              {statusLabel(b)}
            </span>
          </div>
        ))}
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={props.onMore}
          disabled={props.busy}
        >
          {t.mondayImportAnother}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={props.onClose}
          disabled={props.busy}
        >
          {t.mondayClose}
        </Button>
      </DialogFooter>
    </div>
  );
}

function MappingEditor(props: {
  plan: MondayBoardPlan;
  edits: Edits;
  onEdit: (key: string, patch: Partial<ColEdit>) => void;
  onReset: () => void;
}) {
  const { plan, edits, onEdit } = props;

  const typeOf = (key: string): string | null =>
    edits[key]?.include ? edits[key].flow_type : null;

  function mergeTargets(self: MondayPlanColumn): MondayPlanColumn[] {
    const list = plan.columns.filter(
      (c) =>
        c.key !== self.key &&
        edits[c.key]?.include &&
        !edits[c.key]?.merge_into &&
        compatible(typeOf(c.key), typeOf(self.key)),
    );
    // Keep the currently-selected target visible even if it became incompatible.
    const current = edits[self.key]?.merge_into;
    if (current && !list.some((c) => c.key === current)) {
      const sel = plan.columns.find((c) => c.key === current);
      if (sel) list.unshift(sel);
    }
    return list;
  }

  function typeOptions(c: MondayPlanColumn): string[] {
    const detected = c.flow_type ? [c.flow_type] : [];
    return Array.from(new Set([...detected, ...plan.overridable_types]));
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--ink-2)" }}>
          {t.mondayMappingTitle}
          <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>
            {" · "}
            {t.mondayMappingColumnsCount.replace("{count}", String(plan.columns.length))}
          </span>
        </div>
        <button type="button" onClick={props.onReset} style={resetBtnStyle}>
          {t.mondayMappingReset}
        </button>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginBottom: 8, lineHeight: 1.4 }}>
        {t.mondayMappingHint}
      </div>

      {plan.synthetic.length > 0 ? (
        <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginBottom: 8 }}>
          {t.mondayMappingAlways}:{" "}
          {plan.synthetic.map((s) => `${s.name} (${typeLabel(s.flow_type)})`).join(" · ")}
        </div>
      ) : null}

      <div style={{ ...listStyle, maxHeight: 280, gap: 6 }}>
        {plan.columns.map((c) => {
          const e = edits[c.key];
          if (!e) return null;
          const merged = !!e.merge_into;
          const targets = mergeTargets(c);
          const targetName = e.merge_into
            ? (edits[e.merge_into]?.name ??
              plan.columns.find((x) => x.key === e.merge_into)?.flow_name ??
              "")
            : "";
          return (
            <div
              key={c.key}
              style={{
                ...cardStyle,
                opacity: e.include ? 1 : 0.5,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={e.include}
                  onChange={(ev) => onEdit(c.key, { include: ev.target.checked })}
                  title={t.mondayMappingInclude}
                />
                <span
                  style={{
                    fontSize: 12.5,
                    color: "var(--ink-1)",
                    fontWeight: 500,
                    flex: "1 1 auto",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={c.monday_title}
                >
                  {c.monday_title}
                </span>
                <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{c.monday_type}</span>
                {c.origin === "subitem" ? (
                  <span style={badgeStyle}>{t.mondayOptionSubitems.split(" ")[0]}</span>
                ) : null}
              </div>

              {e.include ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginTop: 6,
                    flexWrap: "wrap",
                  }}
                >
                  {merged ? (
                    <div
                      style={{ fontSize: 12, color: "var(--color-brand-blue)", flex: "1 1 auto" }}
                    >
                      {t.mondayMappingMergedInto.replace("{name}", targetName)}
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={e.name}
                        onChange={(ev) => onEdit(c.key, { name: ev.target.value })}
                        placeholder={t.mondayMappingName}
                        autoComplete="off"
                        style={{
                          ...dialogInputStyle,
                          height: 30,
                          flex: "1 1 140px",
                          minWidth: 120,
                        }}
                      />
                      <select
                        value={e.flow_type}
                        onChange={(ev) => onEdit(c.key, { flow_type: ev.target.value })}
                        style={selectStyle}
                        title={t.mondayMappingType}
                      >
                        {typeOptions(c).map((tp) => (
                          <option key={tp} value={tp}>
                            {typeLabel(tp)}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  <select
                    value={e.merge_into ?? ""}
                    onChange={(ev) => onEdit(c.key, { merge_into: ev.target.value || null })}
                    style={selectStyle}
                    title={t.mondayMappingMerge}
                  >
                    <option value="">{t.mondayMappingMergeNone}</option>
                    {targets.map((tg) => (
                      <option key={tg.key} value={tg.key}>
                        {t.mondayMappingMerge}: {edits[tg.key]?.name ?? tg.flow_name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {c.note && c.note !== "übersprungen" ? (
                <div style={{ fontSize: 11, color: "#9a6a2f", marginTop: 4 }}>{c.note}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OptionRow(props: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label
      style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer" }}
    >
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      <span style={{ fontSize: 13, color: "var(--ink-1)" }}>{props.label}</span>
    </label>
  );
}

function ReportStep(props: {
  report: MondayImportReport;
  onOpenBoard: () => void;
  onAnother: () => void;
  onClose: () => void;
}) {
  const r = props.report;
  const stats: Array<[string, number]> = [
    [t.mondayReportColumns, r.columns_created],
    [t.mondayReportRows, r.rows],
    [t.mondayReportSubitems, r.subitems],
    [t.mondayReportCells, r.cells],
    [t.mondayReportComments, r.comments],
    [t.mondayReportFiles, r.files_imported],
  ];
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)", marginBottom: 10 }}>
        {t.mondayImportDone} — {r.board_name}
      </div>
      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}
      >
        {stats.map(([label, value]) => (
          <div key={label} style={statCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 600, color: "var(--ink-1)" }}>{value}</div>
            <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{label}</div>
          </div>
        ))}
      </div>

      {r.files_failed > 0 ? (
        <Warn text={t.mondayReportFilesFailed.replace("{count}", String(r.files_failed))} />
      ) : null}
      {r.unmatched_people.length > 0 ? (
        <Warn text={`${t.mondayReportUnmatchedPeople} ${r.unmatched_people.join(", ")}`} />
      ) : null}
      {r.degraded_columns.length > 0 ? (
        <Warn text={`${t.mondayReportDegraded} ${r.degraded_columns.join(" · ")}`} />
      ) : null}

      <DialogFooter>
        <Button type="button" variant="ghost" size="md" onClick={props.onAnother}>
          {t.mondayImportAnother}
        </Button>
        <Button type="button" variant="primary" size="md" onClick={props.onOpenBoard}>
          {t.mondayOpenBoard}
        </Button>
      </DialogFooter>
    </div>
  );
}

function Warn({ text }: { text: string }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: "var(--ink-2)",
        background: "var(--surface-soft)",
        border: "1px solid var(--hairline)",
        borderRadius: 7,
        padding: "8px 10px",
        marginBottom: 8,
        lineHeight: 1.4,
      }}
    >
      {text}
    </div>
  );
}

const listStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  maxHeight: 200,
  overflowY: "auto",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 6,
};

const boardRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: 7,
  border: "1px solid var(--hairline)",
  cursor: "pointer",
  textAlign: "left",
  width: "100%",
};

const cardStyle: React.CSSProperties = {
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: "8px 10px",
  background: "var(--surface)",
};

const selectStyle: React.CSSProperties = {
  height: 30,
  fontSize: 12.5,
  padding: "0 6px",
  borderRadius: 6,
  border: "1px solid var(--hairline)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  maxWidth: 180,
};

const badgeStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: "var(--ink-4)",
  background: "var(--surface-soft)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "1px 5px",
};

const resetBtnStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: "var(--color-brand-blue)",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 0,
};

const statCardStyle: React.CSSProperties = {
  background: "var(--surface-soft)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: "10px 12px",
  textAlign: "center",
};
